import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { Link as RouterLink } from 'react-router-dom';

const entityLabels = {
  competition: 'Compétitions',
  team: 'Équipes',
  player: 'Joueurs',
  match: 'Matchs',
  gameFact: 'Faits de jeu',
};

const statusConfig = {
  created: { label: 'Créé', color: 'success' },
  updated: { label: 'Modifié', color: 'warning' },
  unchanged: { label: 'Déjà à jour', color: 'default' },
  skipped: { label: 'Non importé', color: 'default' },
  failed: { label: 'Échec', color: 'error' },
};

const getEntityHref = (entityType, targetId) => {
  if (!targetId) return null;
  if (entityType === 'competition') return `/sports/competitions/create/${targetId}`;
  if (entityType === 'team') return `/sports/teams/create/${targetId}`;
  if (entityType === 'player') return `/sports/players/create/${targetId}`;
  if (entityType === 'match') return `/match/${targetId}`;
  return null;
};

const SummaryChips = ({ summary }) => (
  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
    <Chip label={`${summary?.created || 0} création(s)`} color="success" variant="outlined" />
    <Chip label={`${summary?.updated || 0} modification(s)`} color="warning" variant="outlined" />
    <Chip label={`${summary?.unchanged || 0} déjà à jour`} variant="outlined" />
    {(summary?.skipped || 0) > 0 && <Chip label={`${summary?.skipped || 0} non importé(s)`} variant="outlined" />}
    <Chip label={`${summary?.failed || 0} échec(s)`} color={summary?.failed ? 'error' : 'default'} variant="outlined" />
  </Stack>
);

const ExecutionItem = ({ item }) => {
  const config = statusConfig[item?.status] || statusConfig.unchanged;
  const href = getEntityHref(item?.entityType, item?.targetId);

  return (
    <Box sx={{ py: 0.75, '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Chip size="small" label={config.label} color={config.color} variant={['unchanged', 'skipped'].includes(item?.status) ? 'outlined' : 'filled'} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: '1 1 260px' }}>
          {item?.name || item?.sourceId || 'Entité'}
        </Typography>
        {item?.targetId && (
          <Typography variant="caption" color="text.secondary">
            Cible : {item.targetId}
          </Typography>
        )}
        {href && (
          <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Ouvrir
            <OpenInNewOutlinedIcon fontSize="inherit" />
          </Link>
        )}
      </Stack>
      {item?.error && <Alert severity="error" sx={{ mt: 1 }}>{item.error}</Alert>}
    </Box>
  );
};

const GameFactExecutionSummary = ({ summary }) => {
  if (!summary?.strategy) return null;
  const replace = summary.strategy === 'replace';

  return (
    <Alert severity={replace ? 'warning' : 'info'}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Faits de jeu — {replace ? 'remplacement exécuté' : 'Matchs existants préservés'}
      </Typography>
      {replace ? (
        <Stack spacing={0.25}>
          <Typography variant="body2">• {summary.existingTargetMatchesReplaced || 0} Match(s) existants traités en remplacement.</Typography>
          <Typography variant="body2">• {summary.deletedExistingGameFacts || 0} ancien(s) fait(s) de jeu supprimé(s).</Typography>
          <Typography variant="body2">• {summary.createdGameFacts || 0} nouveau(x) fait(s) de jeu créé(s).</Typography>
        </Stack>
      ) : (
        <Stack spacing={0.25}>
          <Typography variant="body2">• {summary.existingTargetMatchesSkipped || 0} Match(s) existants laissés intacts côté faits de jeu.</Typography>
          <Typography variant="body2">• {summary.skippedSnapshotGameFacts || 0} fait(s) du snapshot non importé(s) sur ces Matchs.</Typography>
          <Typography variant="body2">• {summary.createdGameFacts || 0} fait(s) créés pour les nouveaux Matchs.</Typography>
        </Stack>
      )}
    </Alert>
  );
};

const EnvironmentImportExecutionReport = ({ result }) => {
  if (!result) return null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: { xs: 2, md: 3 }, bgcolor: 'background.paper' }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6">6. Résultat de l'import</Typography>
          <Typography variant="body2" color="text.secondary">
            Résultat des écritures effectivement réalisées en base.
          </Typography>
        </Box>

        <Alert severity={result.completed ? 'success' : 'error'}>
          {result.completed
            ? "L'import s'est terminé sans erreur. Relance un pré-check si tu veux vérifier immédiatement l'idempotence ou préparer un réimport."
            : result.partial
            ? "L'import s'est arrêté sur une erreur après certaines écritures. Les suppressions éventuelles sont limitées aux GameFacts remplacés sur des Matchs déjà traités. Relance un pré-check pour repartir de l'état réel."
            : "L'import a échoué avant de pouvoir terminer."}
        </Alert>

        <SummaryChips summary={result.summary} />

        <GameFactExecutionSummary summary={result.gameFacts} />

        {result.reportPersistenceWarning && (
          <Alert severity="warning">{result.reportPersistenceWarning}</Alert>
        )}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {result.reportId && (
            <Button
              variant="contained"
              component={RouterLink}
              to={`/admin/environment-import/reports/${result.reportId}`}
              startIcon={<ListAltOutlinedIcon />}
            >
              Voir ce rapport
            </Button>
          )}
          <Button
            variant="outlined"
            component={RouterLink}
            to="/admin/environment-import/reports"
            startIcon={<ListAltOutlinedIcon />}
          >
            Voir tous les rapports
          </Button>
        </Stack>

        {result.error?.message && (
          <Alert severity="error">
            <Typography variant="subtitle2">Erreur d'exécution</Typography>
            <Typography variant="body2">{result.error.message}</Typography>
          </Alert>
        )}

        <Divider />

        <Stack spacing={1.5}>
          {(result.providers || []).map((providerReport) => (
            <Accordion key={providerReport.provider} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%', pr: 2 }}>
                  <Typography fontWeight={700}>{providerReport.provider}</Typography>
                  <SummaryChips summary={providerReport.summary} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {Object.entries(providerReport.entities || {}).map(([entityType, section]) => (
                    <Accordion key={`${providerReport.provider}-${entityType}`} variant="outlined" disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ width: '100%', pr: 2 }}>
                          <Typography fontWeight={600}>{entityLabels[entityType] || entityType}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {section?.summary?.created || 0} créé(s) · {section?.summary?.updated || 0} modifié(s) · {section?.summary?.unchanged || 0} à jour · {section?.summary?.skipped || 0} non importé(s) · {section?.summary?.failed || 0} échec(s)
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {(section?.items || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Aucune écriture particulière à afficher pour ce type.
                          </Typography>
                        ) : (
                          <Box>
                            {section.items.map((item, index) => (
                              <ExecutionItem key={`${item?.sourceId || index}-${item?.targetProvider}-${index}`} item={item} />
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default EnvironmentImportExecutionReport;
